import { Fragment, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, RotateCw, ShieldAlert } from 'lucide-react';
import { Button, Card, Icon, RefreshButton, cx, typography } from '../ui';
import {
  PanelHeader,
  PanelMessage,
  StatusPill,
  type StatusTone,
} from '../../modules/home/WorkTabsCard';
import { useT } from '../../modules/i18n';
import { argoStatusKind } from '../argocd/ArgoStatusIndicator';
import type { ArgoEnvStatus } from '../argocd/useArgoEnvStatuses';
import type { DeploymentRow, HistoryEntry } from './shared';

/** ArgoCD sync/health classification → foundation StatusPill tone. */
const argoTone = (status: Parameters<typeof argoStatusKind>[0]): StatusTone => {
  const kind = argoStatusKind(status);
  return kind === 'ok' ? 'success' : kind;
};

/**
 * Build the source link label from commitUrl + sourceRepo.
 * Handles both /releases/tag/<tag> and /commit/<sha> shapes.
 * Truncates the last URL segment to 12 characters.
 */
function buildSourceLabel(row: DeploymentRow): string {
  if (!row.commitUrl) return '';
  const segments = row.commitUrl.replace(/\/$/, '').split('/');
  const lastSegment = segments[segments.length - 1] ?? '';
  const truncated = lastSegment.slice(0, 12);
  if (row.sourceRepo) {
    return `${row.sourceRepo}@${truncated}`;
  }
  return truncated;
}

/** Label for a history source link: last URL segment */
function historyLinkLabel(commitUrl: string): string {
  const segments = commitUrl.replace(/\/$/, '').split('/');
  return segments[segments.length - 1] ?? '';
}

const mono = cx(typography.caption, typography.mono);
const headCell = cx(
  typography.overline,
  'whitespace-nowrap px-4 py-2.5 text-left text-fg-tertiary',
);
const bodyCell = cx(
  typography.body,
  'whitespace-nowrap px-4 py-3 align-middle text-fg',
);
const extLink = 'text-brand-text no-underline hover:underline';

/** External link cell (Source / Logs / Traces / Profiles): brand link or em dash. */
function LinkCell({ href, label }: { href?: string; label: string }) {
  if (!href) return <span className="text-fg-muted">—</span>;
  return (
    <a
      className={extLink}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
    </a>
  );
}

/**
 * Telemetry links (Logs / Traces / Profiles) collapsed into a single column,
 * stacked one per line so the column stays narrow. Present destinations are
 * brand links; missing ones are shown muted so the set reads consistently.
 */
function ObservabilityCell({ row }: { row: DeploymentRow }) {
  const t = useT();
  const items: { label: string; href?: string }[] = [
    { label: t('ui.deployments.logsLabel'), href: row.logsUrl },
    { label: t('ui.deployments.tracesLabel'), href: row.tracesUrl },
    { label: t('ui.deployments.profilesLabel'), href: row.profilesUrl },
  ];
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      {items.map(it =>
        it.href ? (
          <a
            key={it.label}
            className={extLink}
            href={it.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {it.label}
          </a>
        ) : (
          <span key={it.label} className="text-fg-muted">
            {it.label}
          </span>
        ),
      )}
    </span>
  );
}

/**
 * Small list of recent promotions for one environment, shown in the
 * expandable detail row of the Deployments table.
 */
export function HistoryPanel({ history }: { history: HistoryEntry[] }) {
  const t = useT();
  if (history.length === 0) {
    return (
      <div className={cx(typography.caption, 'px-6 py-2 text-fg-tertiary')}>
        {t('ui.deployments.noHistory')}
      </div>
    );
  }
  return (
    <div className="py-1 pl-6 pr-4" data-testid="deploy-history">
      {history.map((entry, i) => (
        <div
          key={`${entry.tag}-${i}`}
          className={cx(
            typography.caption,
            'flex items-baseline gap-4 leading-relaxed',
          )}
        >
          <span className={cx(mono, 'min-w-[120px]')}>{entry.tag}</span>
          <span className="text-fg-tertiary">
            {new Date(entry.publishedAt).toLocaleString()}
          </span>
          {entry.commitUrl ? (
            <a
              className={extLink}
              href={entry.commitUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {historyLinkLabel(entry.commitUrl)}
            </a>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * ArgoCD cell content: status pill with a green/amber/red dot — '…' while the
 * status map is still loading (undefined), '—' when the app's status is
 * unknown. Reads from the map the container passes in.
 */
function ArgoStatusCell({
  row,
  statuses,
}: {
  row: DeploymentRow;
  statuses?: Map<string, ArgoEnvStatus>;
}) {
  let label: ReactNode;
  if (!row.argocdApp) {
    label = <span className="text-fg-muted">—</span>;
  } else if (statuses === undefined) {
    label = <span className="text-fg-muted">…</span>;
  } else {
    const status = statuses.get(row.argocdApp);
    label = status ? (
      <StatusPill
        label={`${status.sync} / ${status.health}`}
        tone={argoTone(status)}
      />
    ) : (
      <span className="text-fg-muted">—</span>
    );
  }

  if (!row.argocdUrl) return <>{label}</>;
  return (
    <a
      href={row.argocdUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="no-underline"
    >
      {label}
    </a>
  );
}

/** dev / staging restart immediately; pre-prod / production need approval. */
const rolloutNeedsApproval = (env: string) =>
  env !== 'development' && env !== 'staging';

/**
 * Rollout button for one environment row. Triggers a rolling restart of the
 * service in that env (no-op when no handler is wired, e.g. read-only views).
 * Pre-prod / production show a shield to flag the approval step.
 */
function RolloutCell({
  env,
  onRollout,
  pending,
}: {
  env: string;
  onRollout: (env: string) => void;
  pending: boolean;
}) {
  const t = useT();
  const needsApproval = rolloutNeedsApproval(env);
  return (
    <span
      title={
        needsApproval
          ? t('ui.deployments.rolloutTitleApproval', { env })
          : t('ui.deployments.rolloutTitle', { env })
      }
      className="inline-flex"
    >
      <Button
        variant="tertiary"
        size="sm"
        isDisabled={pending}
        isPending={pending}
        onPress={() => onRollout(env)}
        aria-label={t('ui.deployments.rolloutAriaLabel', { env })}
        iconLeading={
          <Icon
            as={RotateCw}
            size="sm"
            className={cx(pending && 'animate-spin')}
          />
        }
        iconTrailing={
          needsApproval ? (
            <Icon as={ShieldAlert} size="sm" className="text-fg-tertiary" />
          ) : undefined
        }
      >
        {t('ui.deployments.rolloutButton')}
      </Button>
    </span>
  );
}

export interface DeploymentsCardViewProps {
  /** Promoted-tag rows parsed from the entity (one per environment). */
  rows: DeploymentRow[];
  /** Live ArgoCD statuses keyed by app; undefined while loading. */
  argoStatuses?: Map<string, ArgoEnvStatus>;
  lastSync: Date;
  onRefresh: () => void;
  /** Trigger a rolling restart for an environment. Omit to hide the column. */
  onRollout?: (env: string) => void;
  /** Environments with an in-flight rollout (button shows a spinner). */
  rollingOut?: ReadonlySet<string>;
}

/**
 * Presentational Deployments card — promoted tags, source links, live ArgoCD
 * health and an expandable promote-history row per environment. Backstage-free
 * (rows + statuses are passed in) so Storybook can render it; the entity read
 * and ArgoCD fetch live in the DeploymentsCard container.
 */
export function DeploymentsCardView({
  rows,
  argoStatuses,
  lastSync,
  onRefresh,
  onRollout,
  rollingOut,
}: DeploymentsCardViewProps) {
  const t = useT();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const showActions = Boolean(onRollout);
  const columns = [
    t('ui.deployments.columnEnvironment'),
    t('ui.deployments.columnTag'),
    t('ui.deployments.columnSource'),
    t('ui.deployments.columnPublished'),
    t('ui.deployments.columnArgoCD'),
    t('ui.deployments.columnObservability'),
    ...(showActions ? [t('ui.deployments.columnActions')] : []),
  ];

  if (rows.length === 0) {
    return (
      <Card flush>
        <PanelHeader title={t('ui.deployments.title')} />
        <PanelMessage>{t('ui.deployments.emptyMessage')}</PanelMessage>
      </Card>
    );
  }

  return (
    <Card flush>
      <PanelHeader
        title={t('ui.deployments.title')}
        count={`${rows.length} ${
          rows.length === 1
            ? t('ui.deployments.environmentUnit')
            : t('ui.deployments.environmentUnitPlural')
        }`}
        description={t('ui.deployments.description')}
        actions={
          <RefreshButton
            lastSync={lastSync}
            tooltip={t('ui.deployments.refreshTooltip')}
            onRefresh={onRefresh}
          />
        }
      />
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[980px] border-collapse">
          <thead>
            <tr className="border-b border-default bg-muted">
              {columns.map(c => (
                <th key={c} className={headCell}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const isExpanded = Boolean(expanded[row.env]);
              const hasHistory = (row.history?.length ?? 0) > 0;
              return (
                <Fragment key={row.env}>
                  <tr className="border-b border-default last:border-0">
                    <td className={cx(bodyCell, 'font-medium')}>
                      <span className="inline-flex items-center gap-1.5">
                        {hasHistory && (
                          <button
                            type="button"
                            aria-label={t(
                              'ui.deployments.toggleHistoryAriaLabel',
                              { env: row.env },
                            )}
                            aria-expanded={isExpanded}
                            onClick={() =>
                              setExpanded(prev => ({
                                ...prev,
                                [row.env]: !prev[row.env],
                              }))
                            }
                            className="inline-flex cursor-pointer appearance-none rounded bg-transparent p-0.5 text-fg-tertiary outline-none transition-colors hover:text-fg data-[focus-visible]:ring-2 data-[focus-visible]:ring-[rgb(var(--c-ring)/0.4)]"
                          >
                            <Icon
                              as={ChevronDown}
                              size="sm"
                              className={cx(
                                'transition-transform',
                                isExpanded && 'rotate-180',
                              )}
                            />
                          </button>
                        )}
                        {row.env}
                      </span>
                    </td>
                    <td className={bodyCell}>
                      {row.tag ? (
                        <span className={mono}>{row.tag}</span>
                      ) : (
                        <span className="text-fg-muted">—</span>
                      )}
                    </td>
                    <td className={bodyCell}>
                      <LinkCell
                        href={row.commitUrl}
                        label={buildSourceLabel(row)}
                      />
                    </td>
                    <td className={cx(bodyCell, 'text-fg-tertiary')}>
                      {row.publishedAt
                        ? new Date(row.publishedAt).toLocaleString()
                        : '—'}
                    </td>
                    <td className={bodyCell}>
                      <ArgoStatusCell row={row} statuses={argoStatuses} />
                    </td>
                    <td className={bodyCell}>
                      <ObservabilityCell row={row} />
                    </td>
                    {showActions && onRollout && (
                      <td className={bodyCell}>
                        <RolloutCell
                          env={row.env}
                          onRollout={onRollout}
                          pending={Boolean(rollingOut?.has(row.env))}
                        />
                      </td>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td
                        className="border-b border-default bg-muted p-0"
                        colSpan={columns.length}
                      >
                        <HistoryPanel history={row.history ?? []} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
